import { useCallback, useEffect, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Connection,
  Edge,
  Node,
  NodeTypes,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus } from "lucide-react";
import AiConversationStartNode from "../nodes/AiConversationStartNode";
import AiConversationNode from "../nodes/AiConversationNode";
import AiNodeEditorDialog from "./AiNodeEditorDialog";
import React from "react";
import type { AiNodeActionType, AiConversationNodeData } from "@/model/interface/AiConversationNode";
import { AI_NODE_ACTION_TYPES } from "@/model/interface/AiConversationNode";

const nodeTypes: NodeTypes = {
  conversationStart: AiConversationStartNode,
  conversationStep: AiConversationNode,
};

interface AiConversationFlowBuilderProps {
  initialNodes?: Node[];
  initialEdges?: Edge[];
  onChange?: (nodes: Node[], edges: Edge[]) => void;
}

export default function AiConversationFlowBuilder({
  initialNodes = [],
  initialEdges = [],
  onChange,
}: AiConversationFlowBuilderProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialNodes.length > 0
      ? initialNodes
      : [
          {
            id: "start",
            type: "conversationStart",
            position: { x: 250, y: 50 },
            data: {},
          },
        ],
  );

  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const [editingNode, setEditingNode] = useState<{
    nodeId: string;
    actionType: AiNodeActionType;
    data?: Partial<AiConversationNodeData>;
  } | null>(null);

  const onConnect = useCallback(
    (params: Connection) =>
      setEdges((currentEdges) =>
        addEdge(
          {
            ...params,
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          currentEdges,
        ),
      ),
    [setEdges],
  );

  const getNodeLabel = (actionType: AiNodeActionType, data: Partial<AiConversationNodeData>): string => {
    switch (actionType) {
      case "GREETING":
        return data.message ? `"${data.message.substring(0, 25)}..."` : "Greeting";
      case "SAY_MESSAGE":
        return data.message ? `"${data.message.substring(0, 25)}..."` : "Say Message";
      case "ASK_QUESTION":
        return data.questionPrompt ? `Ask: "${data.questionPrompt.substring(0, 20)}..."` : "Ask Question";
      case "CONDITION":
        return data.conditionField ? `If ${data.conditionField} ${data.conditionOperator} ${data.conditionValue}` : "Condition";
      case "TOOL_CALL":
        return data.toolName || "Tool Call";
      case "TRANSFER":
        return data.transferTarget || "Transfer";
      case "SET_OUTCOME":
        return data.outcomeName || "Set Outcome";
      case "END_CONVERSATION":
        return data.message ? `"${data.message.substring(0, 25)}..."` : "End";
      default:
        return "Step";
    }
  };

  const handleEditNode = (nodeId: string, actionType: AiNodeActionType) => {
    const node = nodes.find((existingNode) => existingNode.id === nodeId);
    setEditingNode({
      nodeId,
      actionType,
      data: node?.data as Partial<AiConversationNodeData>,
    });
  };

  const handleDeleteNode = (nodeId: string) => {
    setNodes((currentNodes) => currentNodes.filter((node) => node.id !== nodeId));
    setEdges((currentEdges) => currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  };

  const addNode = (actionType: AiNodeActionType) => {
    const nodeId = `node-${Date.now()}`;

    const newNode: Node = {
      id: nodeId,
      type: "conversationStep",
      position: { x: 225, y: nodes.length * 150 + 100 },
      data: {
        actionType,
        label: AI_NODE_ACTION_TYPES.find((type) => type.value === actionType)?.label || "Step",
        onEdit: () => handleEditNode(nodeId, actionType),
        onDelete: () => handleDeleteNode(nodeId),
      } as AiConversationNodeData,
    };

    setNodes((currentNodes) => [...currentNodes, newNode]);

    if (nodes.length === 1) {
      const startNode = nodes.find((existingNode) => existingNode.type === "conversationStart");
      if (startNode) {
        setEdges((currentEdges) => [
          ...currentEdges,
          {
            id: `edge-${startNode.id}-${nodeId}`,
            source: startNode.id,
            target: nodeId,
            markerEnd: { type: MarkerType.ArrowClosed },
          },
        ]);
      }
    }

    setEditingNode({ nodeId, actionType });
  };

  const handleSaveNodeData = (data: Partial<AiConversationNodeData>) => {
    if (!editingNode) return;

    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (node.id === editingNode.nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              ...data,
              actionType: editingNode.actionType,
              label: getNodeLabel(editingNode.actionType, data),
              onEdit: () => handleEditNode(node.id, editingNode.actionType),
              onDelete: () => handleDeleteNode(node.id),
            } as AiConversationNodeData,
          };
        }
        return node;
      }),
    );

    setEditingNode(null);
  };

  useEffect(() => {
    if (initialNodes.length === 0) return;
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        if (node.type === "conversationStart") return node;
        const nodeData = node.data as AiConversationNodeData;
        return {
          ...node,
          data: {
            ...nodeData,
            onEdit: () => handleEditNode(node.id, nodeData.actionType),
            onDelete: () => handleDeleteNode(node.id),
          },
        };
      }),
    );
  }, []);

  useEffect(() => {
    onChange?.(nodes, edges);
  }, [nodes, edges]);

  return (
    <div className="h-150 w-full border rounded-lg relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        defaultViewport={{ x: 100, y: 100, zoom: 1 }}
        className="bg-muted/20"
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>

      <div className="absolute top-4 right-4 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Step
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => addNode("GREETING")}>Greeting</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("SAY_MESSAGE")}>Say Message</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("ASK_QUESTION")}>Ask Question</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => addNode("CONDITION")}>Condition Branch</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("TOOL_CALL")}>Tool Call</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => addNode("TRANSFER")}>Transfer Call</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("SET_OUTCOME")}>Set Outcome</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addNode("END_CONVERSATION")}>End Conversation</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {editingNode && (
        <AiNodeEditorDialog
          open={!!editingNode}
          onOpenChange={(isOpen) => !isOpen && setEditingNode(null)}
          actionType={editingNode.actionType}
          initialData={editingNode.data}
          onSave={handleSaveNodeData}
        />
      )}
    </div>
  );
}
